/**
 * U6タグのエントリーポイント。
 * NFR Design 1.2: Bulkhead per Tag Instance（タグ単位の隔壁化）。
 * 各<script>要素の実行を完全に独立させ、1タグの初期化失敗が他タグ・既存ページに伝播しないようにする。
 * NFR Design 1.3: 非ブロッキング非同期初期化。
 * BR-U6-3（H-3/H-4修正、2026-07-15）: displayTriggerが存在する場合は表示条件監視の
 * コールバック内でのみポップアップをレンダリングする。displayConditionMonitorはタグごとに
 * 独立したインスタンスを生成する（createDisplayConditionMonitor、H-2対応）。
 */

import { readWidgetContext, fetchScenario } from "./w1-survey/fetchScenario";
import { renderSurveyPopup } from "./w1-survey/surveyPopupUi";
import { createDisplayConditionMonitor } from "./w1-survey/displayConditionMonitor";
import { renderPreBookingForm } from "./w2-prebooking/preBookingFormUi";
import { detectCancelModalTrigger } from "./w4-appointment/cancelAppointmentAsVisitor";
import { renderAppointmentCancelModal } from "./w4-appointment/appointmentConfirmationUi";
import { renderMultiCvAppointmentList } from "./multi-cv/multiCvAppointmentListUi";
import { consolidateByCompany } from "./multi-cv/consolidateByCompany";
import { showErrorMessageBanner } from "./core-ui/errorMessageBannerUi";
import { logClientWarning } from "./core/errorHandler";
import { createTagCache } from "./core/tagCache";
import { registerTagMonitor } from "./core/hostApi";
import { trackPageEvent } from "./w3-tracking/trackPageEvent";
import { getOrCreateSessionId } from "./w3-tracking/sessionContext";
import type { ContentDisplayResult } from "./types";

/**
 * 各<script>タグの初期化処理。タグ単位で完全に独立させる（NFR Design 1.2）。
 */
async function initializeTag(scriptElement: HTMLScriptElement): Promise<void> {
  // タグ単位メモリキャッシュ・表示条件監視（NFR Design 1.2/3.1、いずれもタグ間共有なし）
  const cache = createTagCache<string, ContentDisplayResult[]>();
  const monitor = createDisplayConditionMonitor();

  try {
    const context = readWidgetContext(scriptElement);
    if (!context) return;

    // M-2: ホスト側（電子ブックビューア等）がwindow.SpicaWidget.notifyPageTransitionを呼び出す際、
    // このタグのmonitorにページ推移を伝えられるようtagIdで登録する（core/hostApi.ts参照）。
    // 2026-08-07 バグ修正: シナリオ解決API待ちの間に届いたページ推移を取りこぼさないよう、
    // await前（同期処理のうち）に登録する。monitor側でstart()前の通知を保持して評価する。
    const tagId = context.cvs.map((cv) => cv.objectRootId).join(",");
    registerTagMonitor(tagId, monitor);

    const fetchResult = await fetchScenario(context);
    if (!fetchResult.success || !fetchResult.result) {
      showErrorMessageBanner({
        errorType: "SYSTEM",
        message: "時間をおいて再度お試しください。",
        onRetry: () => initializeTag(scriptElement),
      });
      return;
    }

    const { results, displayTrigger, displayParams } = fetchResult.result;
    cache.set(context.screenType, results);

    // 2026-08-03: 同一出展企業に属する複数CVは1件に統合する（consolidateByCompany参照）。
    // US-D2-10の想定「企業ごとに1つの商談機会を提示する」を満たすための後処理。
    const visibleResults = consolidateByCompany(
      results.filter((r) => r.displayMode !== "HIDDEN"),
      context.cvs
    );
    if (visibleResults.length === 0) return;

    const renderResults = () => {
      renderResultsForContext(visibleResults, context.screenType);
    };

    if (displayTrigger) {
      // BR-U6-3: 表示条件を満たすまでポップアップUI自体をDOMに構築しない
      monitor.start(displayTrigger, displayParams, renderResults);
    } else {
      renderResults();
    }
  } catch {
    // このタグ内で発生したエラーが既存ページに伝播しないようにする（NFR Design 1.2）
    logClientWarning("initializeTag failed unexpectedly");
  }
}

/**
 * ウィジェット（アンケート表示・アポ打診フォーム）が実際に表示される直前にWIDGET_VIEWイベントを
 * 発火する（BR-U8-21確定、U13 site_type統一Unit）。
 *
 * <p>displayMode=SURVEY_AND_APPOINTMENT/APPOINTMENT_ONLYの場合のみ発火する（HIDDENは対象外、
 * ただしvisibleResultsは既にHIDDEN除外済み）。SURVEY_AND_APPOINTMENTは1回の表示で1件
 * （アンケート・アポ打診を分けて2件カウントしない）。CVごとに個別発火する（複数CV同時表示時）。
 *
 * <p><b>単一CV・APPOINTMENT_ONLYのmemberGroupId欠如ガード（レビュー指摘対応）</b>:
 * 単一CV表示時、renderResultsForContextはdisplayMode=APPOINTMENT_ONLYであってもmemberGroupIdが
 * 無い場合は何もレンダリングしない（既存挙動）。WIDGET_VIEWは「実際に表示される直前」に発火する
 * 契約のため、この場合は発火しないようガードする。複数CV表示時（renderMultiCvAppointmentList）は
 * memberGroupId無しでもリストアイテム自体は描画されるため対象外（ガード不要）。
 */
export function trackWidgetViewEvents(
  visibleResults: ContentDisplayResult[],
  screenType: string
): void {
  const isSingleCv = visibleResults.length === 1;
  // 効果分析ログ（BR-AN-2/BR-AN-3(b)）: 既存 WIDGET_VIEW をファネル起点として流用し、sessionId を
  // 付与して後続イベントと相関できるようにする（起点イベントは新設しない）。同一訪問のため sessionId は共通。
  const sessionId = getOrCreateSessionId();
  for (const result of visibleResults) {
    if (result.displayMode !== "SURVEY_AND_APPOINTMENT" && result.displayMode !== "APPOINTMENT_ONLY") {
      continue;
    }
    if (isSingleCv && result.displayMode === "APPOINTMENT_ONLY" && !result.memberGroupId) {
      // 単一CV・APPOINTMENT_ONLYではmemberGroupId無しの場合UIが描画されない（renderResultsForContext参照）
      continue;
    }
    trackPageEvent("WIDGET_VIEW", {
      objectType: result.objectType,
      objectRootId: result.objectRootId,
      companyRootId: result.companyRootId,
      screenType,
      prFlg: result.prFlg,
      isMainCv: result.isMainCv,
      sessionId,
    });
  }
}

/**
 * fetchScenarioの結果をもとに、実際のUIをレンダリングする。
 * displayTriggerが無い場合は即時、ある場合は監視コールバックから呼ばれる。
 *
 * <p><b>WIDGET_VIEWの発火位置（レビュー指摘対応、2026-08-17）</b>: 以前は本関数の呼び出しと
 * 同時にtrackWidgetViewEventsを呼んでいたが、これは複数CV経路（空き枠取得の非同期完了を待たない）・
 * 単一CV経路（未ログイン時にUIを構築せずreturnする、APPOINTMENT_ONLYでmemberGroupId欠如時に
 * 何も描画しない）のいずれでも、実際にUIが構築される前に計上してしまう問題があった。
 * BR-U8-21の確定仕様「実際にウィジェットUIがDOMに構築・表示された時点」に合わせ、各分岐の
 * 実際のDOM構築が完了した直後にのみtrackWidgetViewEventsを呼ぶよう変更した。
 */
function renderResultsForContext(
  visibleResults: ContentDisplayResult[],
  screenType: string
): void {
  // 2026-08-21 バグ修正: カタログDL完了画面（CATALOG_DOWNLOAD_COMPLETE）は設計上常に
  // center-modalで表示する（frontend-components.md WidgetPanel配置バリアント参照）。
  // consolidateByCompanyにより複数CVが同一企業として1件に統合された場合でも、
  // 単一CVパス（bottom-left）に入らないようscreenTypeで判定する。
  if (visibleResults.length > 1 || screenType === "CATALOG_DOWNLOAD_COMPLETE") {
    // US-D2-10: 複数CV併記表示（2026-08-19デザイン更新: 画面中央モーダル。ログイン誘導・
    // 予約確定後の完了表示はWidgetPanel/BookingSection内で完結するため、コールバックは不要）。
    // ローディングインジケーター対応: パネルを先に構築しスピナーを表示してから空き枠を取得する。
    void (async () => {
      await renderMultiCvAppointmentList(visibleResults, screenType);
      // renderMultiCvAppointmentListは未ログイン時も各CVアイテムをモザイク表示するため
      // （BR-U6-1）、複数CV経路は空き枠取得完了後のリスト自体の構築成功をもって計上する。
      trackWidgetViewEvents(visibleResults, screenType);
    })();
    return;
  }

  const singleResult = visibleResults[0];
  if (singleResult.displayMode === "SURVEY_AND_APPOINTMENT") {
    // 2026-08-19デザイン更新: renderSurveyPopupはWidgetPanel内でログイン誘導・アンケート回答後の
    // 日程調整UI遷移まで自己完結する（コールバック不要）。未ログイン時もパネル自体は構築される
    // ため（ログインゲートを重畳表示）、WIDGET_VIEWはパネル構築の時点で計上する。
    renderSurveyPopup(singleResult, screenType);
    trackWidgetViewEvents(visibleResults, screenType);
  } else if (singleResult.displayMode === "APPOINTMENT_ONLY" && singleResult.memberGroupId) {
    void renderPreBookingForm(
      singleResult.memberGroupId,
      screenType,
      singleResult.objectType,
      singleResult.objectRootId,
      singleResult.meetingModePolicy,
      singleResult.prFlg,
      singleResult.companyName,
      singleResult.contentName,
      singleResult.isMainCv,
      singleResult.companyRootId
    ).then(() => {
      // renderPreBookingFormは未ログインでもフォーム自体を構築する（BR-U6-2、事前予約フォームは
      // 未ログインでも表示可）。空き枠取得完了後にDOM構築が完了する時点で計上する。
      trackWidgetViewEvents(visibleResults, screenType);
    });
  }
  // APPOINTMENT_ONLYでmemberGroupId欠如の場合は何も描画されないため、上記いずれの
  // 分岐にも該当せずtrackWidgetViewEventsは呼ばれない（レビュー指摘対応）。
}

/**
 * BR-U6-11: トップページ常時埋め込み時のキャンセルモーダル起動判定。
 */
function initializeCancelModal(): void {
  try {
    const appointmentId = detectCancelModalTrigger();
    if (appointmentId !== null) {
      void renderAppointmentCancelModal(appointmentId);
    }
  } catch {
    logClientWarning("initializeCancelModal failed unexpectedly");
  }
}

// タグ本体の初期化（data-cvsを持つscript要素の場合）
const currentScript = document.currentScript as HTMLScriptElement | null;
if (currentScript?.dataset.cvs) {
  // 非ブロッキング（NFR Design 1.3）
  void initializeTag(currentScript);
}

// BR-U6-11: キャンセルモーダルはURLパラメータで常時判定
initializeCancelModal();
